/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-tagless-components, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { debounce } from '@ember/runloop';
import { modifier } from 'ember-modifier';
import { serializeQueryParams } from 'linced-app/utils/url';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import RelationshipEnum from '@linc-technologies/linced-common/enums/medical-relationship';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import MedicalAccess from 'linced-app/enums/medical-access';
import {
  findBy,
  mapBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { loadPeople } from '@linc-technologies/linced-common/utils/load-people';
import ENV from 'linced-app/config/environment';
import { observes } from '@ember-decorators/object';
import { restartableTask } from 'ember-concurrency';

export default class MedicalContactManager extends Component {
  @action
  setEndDate(value) {
    this.endDate = value;
  }

  @action
  setMedicalContactPriority(value) {
    this.medicalContactPriority = value;
  }

  @action
  setMedicalContactRelationship(value) {
    this.medicalContactRelationship = value;
  }

  @action
  setNotes(value) {
    this.notes = value;
  }

  @action
  setPermissionToContact(value) {
    this.permissionToContact = value;
  }

  @action
  setQuery(value) {
    this.query = value;
  }

  @action
  setRoleRequired(value) {
    this.roleRequired = value;
  }

  @action
  setStartDate(value) {
    this.startDate = value;
  }

  // Services
  @service
  fetch;

  @service
  options;

  @service
  permissions;

  @service
  schoolSchema;

  @service
  session;

  @service
  store;

  @service
  toaster;

  Jurisdiction = Jurisdiction;
  PersonType = PersonType;
  MedicalAccess = MedicalAccess;
  Scopes = Scopes;
  @tracked readOnly = false;

  @alias('session.school.jurisdiction')
  jurisdiction;

  // loadOnInsert loads the medical contacts when the component is in the DOM,
  // and again when the person changes.
  loadOnInsert = modifier((_element, [person]) => {
    if (person) {
      this.loadContacts.perform();
    }
  });

  // loadContacts loads the medical contacts that the cards show.
  loadContacts = restartableTask(async () => {
    await loadPeople(
      this.store,
      mapBy(this.person.learner?.medicalContacts ?? [], 'medicalContactId'),
      { type: PersonType.DOCTORS_AND_SPECIALISTS },
    );
  });

  @computed('fieldGroups.[]')
  get groups() {
    return this.fieldGroups.filter(
      (group) => group.personType === PersonType.DOCTORS_AND_SPECIALISTS,
    );
  }

  @computed('jurisdiction', 'readOnly', 'session.scopes.[]')
  get canAddMedicalContacts() {
    return (
      this.jurisdiction !== Jurisdiction.AU_NSW &&
      !this.readOnly &&
      this.permissions.meet(Scopes.TENANT_ADMIN)
    );
  }

  @computed('groups.[]')
  get relationshipGroup() {
    return this.groups.find((group) => group.key === 'relationship');
  }

  @computed('groups.[]')
  get medicalFieldGroups() {
    return this.groups.filter((group) => group.key !== 'relationship');
  }

  @tracked
  contactDialogOpen = false;
  @tracked
  contactDetailsOpen = false;
  @tracked
  editingContact = false;
  @tracked
  medicalContactPriority = 0;
  @tracked
  medicalContactRelationship = null;
  @tracked
  notes = '';
  @tracked
  startDate = '';
  @tracked
  endDate = '';
  @tracked
  permissionToContact = false;
  @tracked
  roleRequired = Object.freeze({ label: 'None', value: 0 });
  @tracked
  selectedContact = null;
  @tracked
  contact = null;
  @tracked
  contactToRemove = null;
  @tracked
  confirmRemoveContactDialogOpen = false;
  @tracked query = '';
  @tracked
  searching = false;
  @tracked
  searchResults = [];
  roleOptionTypes = MedicalAccess.values;
  relationshipTypes = RelationshipEnum.values;

  @tracked
  medicalContactFirstName;

  @tracked
  medicalContactLastName;

  @observes('query')
  _searchTermChanged() {
    if (this.validSearchTerm) {
      // Set "searching" flag so that we don't get a flash of "no results found" before the debounced search.
      this.searching = true;
    }
    debounce(this, this.searchContacts, 350);
  }

  get validSearchTerm() {
    return !isEmpty(this.query);
  }

  // sort medical contacts by priority, expired contacts go at the back regardless of priority
  @computed('person.learner.medicalContacts.[]')
  get sortedContacts() {
    let sortedContacts = [];
    let expiredContacts = [];
    let prioritySortedContacts = sortBy(
      this.person.learner.medicalContacts,
      'priority',
    );
    prioritySortedContacts.forEach((contact) => {
      if (contact.isCurrent) {
        sortedContacts.push(contact);
      } else {
        expiredContacts.push(contact);
      }
    });
    return sortedContacts.concat(expiredContacts);
  }

  @action
  addContact() {
    this.contactDialogOpen = true;
    this.editingContact = false;
    this.medicalContactFirstName = '';
    this.medicalContactLastName = '';
    this.medicalContactRelationship = null;
  }

  @action
  editContact(contact) {
    let selectedContact = this.store.peekRecord(
      'person',
      contact.medicalContactId,
    );
    this.selectedContact = selectedContact;
    this.medicalContactRelationship = findBy(
      this.relationshipTypes,
      'value',
      contact.relationship,
    );
    this.medicalContactPriority = contact.priority;
    this.notes = contact.notes;
    this.startDate = contact.startDate;
    this.endDate = contact.endDate;
    this.permissionToContact = contact.permissionToContact;
    this.roleRequired = findBy(
      this.roleOptionTypes,
      'value',
      contact.roleRequired,
    );
    this.contact = contact;
    this.editingContact = true;
    this.contactDialogOpen = true;
  }

  @action
  closeContactDialog() {
    if (this.contact && this.contact.hasDirtyAttributes) {
      this.contact.rollbackAttributes();
    }
    this.contactDialogOpen = false;
    this.reset();
  }

  @action
  saveContact() {
    let contact = this.contact;

    // Set the bulk of the properties.
    contact.setProperties({
      relationship: this.get('medicalContactRelationship.value'),
      notes: this.notes,
      priority: this.medicalContactPriority,
      startDate: this.startDate,
      endDate: this.endDate,
      permissionToContact: this.permissionToContact,
      roleRequired: this.get('roleRequired.value'),
    });

    this.get('person.learner.medicalContacts').addFragment(this.contact);
    this.contactDialogOpen = false;
    this.reset();
  }

  @action
  newContact() {
    let person = this.schoolSchema.newPerson(
      PersonType.DOCTORS_AND_SPECIALISTS,
    );

    this.selectedContact = person;
    this.contactDetailsOpen = true;
  }

  @action
  selectSearchResult(result) {
    this.store
      .findRecord('person', result.personId)
      .then((contact) => this.selectContact(contact));
  }

  @action
  showConfirmRemoveContact(contact) {
    this.contactToRemove = contact;
    this.confirmRemoveContactDialogOpen = true;
  }

  @action
  confirmRemoveContact(shouldRemove) {
    if (shouldRemove) {
      let contact = this.contactToRemove;
      this.get('person.learner.medicalContacts').removeFragment(contact);
    }
    this.contactToRemove = null;
    this.confirmRemoveContactDialogOpen = false;
  }

  @action
  closeContactDetails() {
    this.contactDetailsOpen = false;
  }

  searchContacts() {
    if (!this.validSearchTerm) {
      // If the search term isn't valid, abort the search.
      this.resetSearchResults();
      return;
    }

    let params = {
      query: this.query,
    };

    this.searching = true;
    this.fetch
      .request(
        `${ENV.APP.apiHost}/people/v4/medical/:search?${serializeQueryParams(
          params,
        )}`,
      )
      .then((response) => {
        this.searching = false;
        this.searchResults = response.medicalContacts;
      });
  }

  resetSearchResults() {
    this.searchResults = [];
    this.searching = false;
  }

  resetSearchForm() {
    this.setProperties({
      query: '',
    });
  }

  resetForm() {
    this.medicalContactPriority = 0;
    this.medicalContactRelationship = 0;
    this.notes = '';
    this.startDate = '';
    this.endDate = '';
    this.permissionToContact = false;
    this.roleRequired = Object.freeze({ label: 'None', value: 0 });
    this.selectedContact = null;
    this.contact = null;
  }

  @action
  reset() {
    this.resetSearchResults();
    this.resetForm();
    this.resetSearchForm();
  }

  @action
  selectContact(contact) {
    let fragment = this.store.createFragment('people/medical-contact', {
      medicalContactId: contact.get('id'),
    });
    this.contact = fragment;
    this.selectedContact = contact;
    this.closeContactDetails();
  }
}
